import type { ConditionalSelectDef } from '../types';

interface ConditionalSelectFieldProps {
  question: ConditionalSelectDef;
  selections: Record<string, string>;
  secondarySelections: Record<string, Record<string, string>>;
  onChange: (
    selections: Record<string, string>,
    secondarySelections: Record<string, Record<string, string>>,
  ) => void;
}

/**
 * ConditionalSelectField
 * 各アイテムのカード表示、primaryOptions の2x2グリッド、
 * 条件付きで secondaryOptions を表示
 * 完了チェックマーク表示
 */
export function ConditionalSelectField({
  question,
  selections,
  secondarySelections,
  onChange,
}: ConditionalSelectFieldProps) {
  const handlePrimarySelect = (itemId: string, optionId: string) => {
    const newSelections = { ...selections, [itemId]: optionId };
    const newSecondary = { ...secondarySelections };

    // 条件に合致しない場合はセカンダリをクリア
    const matchingRule = question.conditionalRules.find(
      (rule) => rule.triggerValue === optionId,
    );
    if (!matchingRule) {
      delete newSecondary[itemId];
    }

    onChange(newSelections, newSecondary);
  };

  const handleSecondarySelect = (
    itemId: string,
    ruleKey: string,
    optionId: string,
  ) => {
    const newSecondary = {
      ...secondarySelections,
      [itemId]: {
        ...secondarySelections[itemId],
        [ruleKey]: optionId,
      },
    };
    onChange(selections, newSecondary);
  };

  return (
    <div className="space-y-4">
      {question.items.map((item) => {
        const primaryValue = selections[item.id];
        const matchingRule = question.conditionalRules.find(
          (rule) => rule.triggerValue === primaryValue,
        );
        const hasSecondarySelection =
          matchingRule &&
          secondarySelections[item.id]?.[matchingRule.triggerValue];
        const isComplete =
          primaryValue != null && (!matchingRule || hasSecondarySelection);

        return (
          <div
            key={item.id}
            className={`bg-white rounded-lg p-4 transition-all ${
              isComplete
                ? 'border-2 border-[#0052FF] shadow-sm'
                : 'border border-[#E5E7EB]'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-[#111827] text-sm">
                {item.label}
              </div>
              {isComplete && (
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#0052FF] flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* Primary options (2x2 grid) */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {question.primaryOptions.map((option) => {
                const isSelected = primaryValue === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => handlePrimarySelect(item.id, option.id)}
                    className={`px-3 py-2 rounded-md text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                      isSelected
                        ? 'border-2 border-[#0052FF] bg-[#0052FF] text-white shadow-md'
                        : 'border border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#0052FF] hover:bg-[#F0F5FF]'
                    }`}
                  >
                    {isSelected && (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Secondary options (conditional) */}
            {matchingRule && (
              <div className="pt-3 mt-3 border-t border-[#E5E7EB]">
                {matchingRule.secondaryLabel && (
                  <div className="text-xs text-[#6B7280] font-semibold mb-2">
                    {matchingRule.secondaryLabel}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {matchingRule.secondaryOptions.map((option) => {
                    const isSelected =
                      secondarySelections[item.id]?.[matchingRule.triggerValue] ===
                      option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() =>
                          handleSecondarySelect(
                            item.id,
                            matchingRule.triggerValue,
                            option.id,
                          )
                        }
                        className={`px-2.5 py-2 rounded text-xs font-semibold transition-all flex items-center justify-center gap-1 ${
                          isSelected
                            ? 'border-2 border-[#0052FF] bg-[#F0F5FF] text-[#0052FF]'
                            : 'border border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#0052FF] hover:bg-[#F0F5FF]/50'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
